/**
 * Verifies and applies replicated events into a [ReplicatedEventStore].
 *
 * The two object kinds are checked very differently, which is the whole point of the split:
 *
 * - A [LinkPreimage] is accepted on self-consistency alone (`projectLink(parent, target) == projection`). Its own
 *   parent/target preimages may still be absent — that is a normal missing observation, not an error.
 * - A [SignedCommitment] is accepted only once the issuer credential resolves, the signature verifies against it, and
 *   the head attributes to that credential's actor. A withheld *body* never blocks attribution, so the header stays
 *   acceptable while its content remains a missing observation for others.
 *
 * Any genuinely-absent preimage yields [ReplicatedEventApplier.ApplyResult.Pending] naming the specific missing projection,
 * which a future sync layer can turn into a fetch. The applier keeps no pending queue:
 * re-application is the caller's job, and the out-of-order engine that owns it is a Milestone 3 concern.
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.Credential
import com.oasisfeng.invaluable.actor.CredentialProof
import com.oasisfeng.invaluable.actor.CredentialProtocol
import com.oasisfeng.invaluable.actor.ObservedCommitment
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.memoizing
import com.oasisfeng.invaluable.data.resolveLink

/**
 * @param verifyProof credential-protocol signature verifier; defaults to Ed25519, the only MVP credential protocol.
 */
class ReplicatedEventApplier(
    private val store: ReplicatedEventStore,
    private val verifyProof: (Credential, ByteArray, CredentialProof) -> Boolean = Ed25519Credential::verify,
) {
    /** Outcome of applying one [ReplicatedEvent] to a [ReplicatedEventStore]. */
    sealed interface ApplyResult {
        data object Applied : ApplyResult
        data object Duplicate : ApplyResult
        /** Verification cannot complete until these preimage projections arrive. */
        data class Pending(val missing: List<LinkProjection>) : ApplyResult
        data class Invalid(val reason: String) : ApplyResult
    }

    private val projector get() = store.projector

    fun apply(event: ReplicatedEvent): ApplyResult = when (event) {
        is LinkPreimage -> applyPreimage(event)
        is SignedCommitment -> applyAttestation(event)
        is SealedContent -> applySealed(event)
    }

    /**
     * A sealed ciphertext is accepted on possession alone — it carries no claim an observer can verify without the key.
     * Its [SealedContent.contentProjection] is merely an *index hint* for resolution; the real integrity check happens
     * when a capability holder opens it (`combine` then `project == projection`). So unlike a preimage there is nothing
     * to reject here beyond a scheme mismatch: a junk seal simply never resolves, while still being a holdable carrier.
     */
    private fun applySealed(event: SealedContent): ApplyResult = when {
        event.contentProjection.scheme != projector.scheme ->
            ApplyResult.Invalid("Sealed content projection scheme does not match store scheme.")
        else -> if (store.appendSealed(event)) ApplyResult.Applied else ApplyResult.Duplicate
    }

    private fun applyPreimage(event: LinkPreimage): ApplyResult = event.record.let { record -> when {
        record.projection.scheme != projector.scheme ->
            ApplyResult.Invalid("Preimage projection scheme does not match store scheme.")
        projector.projectLink(record.parent, record.target) != record.projection ->
            ApplyResult.Invalid("Preimage projection does not match its endpoints.")
        else -> if (store.appendPreimage(record)) ApplyResult.Applied else ApplyResult.Duplicate
    }}

    private fun applyAttestation(event: SignedCommitment): ApplyResult {
        if (event.head.scheme != projector.scheme || event.credential.scheme != projector.scheme)
            return ApplyResult.Invalid("Attestation projection scheme does not match store scheme.")
        if (store.containsAttestation(event.head)) return ApplyResult.Duplicate

        // Credential and head are resolved against the same records within this one call, and the head's ancestry is
        // most of both walks — one memo for the call, discarded with it (see [memoizing]).
        val resolver = store.resolver().memoizing()

        // 1. Resolve the issuer credential (required to recover the verification key).
        val credential = when (val resolved = projector.resolveLink(event.credential, resolver)) {
            is Observation.Missing -> return ApplyResult.Pending(listOf(resolved.projection))
            is Observation.Invalid -> return ApplyResult.Invalid(resolved.reason)
            is Observation.Known -> resolved.value.nestedOrNull()?.let(Credential::render)
                ?: return ApplyResult.Invalid("Attested credential does not decode as a credential.")
        }

        // 2. Verify the signature against the credential. A malformed signature must be rejected, never thrown:
        // this applier consumes untrusted network input, so a verifier that throws on bad bytes means "invalid".
        val protocol = credential.link.parent().nestedOrNull()?.let(CredentialProtocol::render)
            ?: return ApplyResult.Invalid("Credential protocol is not a valid credential protocol.")
        val signatureValid = runCatching {
            verifyProof(credential, event.canonicalBytes(), CredentialProof(protocol, event.signature))
        }.getOrDefault(false)
        if (! signatureValid)
            return ApplyResult.Invalid("Commitment signature does not verify against the attested credential.")

        // 3. Attribute the head to the credential's actor. A withheld committed body does not block this.
        when (val issuer = ObservedCommitment(projector, event.head, resolver).issuer) {
            is Observation.Missing -> return ApplyResult.Pending(listOf(issuer.projection))
            is Observation.Invalid -> return ApplyResult.Invalid(issuer.reason)
            is Observation.Known -> if (issuer.value.link != Actor(credential).link)
                return ApplyResult.Invalid("Attested head is not issued by the credential's actor.")
        }

        return if (store.appendAttestation(event)) ApplyResult.Applied else ApplyResult.Duplicate
    }
}
